<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { DEV_SESSION_ENABLED } from '@/utils/devSession';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { NEllipsis } from 'naive-ui';
import AppIcon from '@/components/ui/AppIcon.vue';
import Avatar from '@/components/ui/Avatar.vue';
import ThemeToggle from '@/components/ui/ThemeToggle.vue';
import IndexCard from '@/components/library/IndexCard.vue';
import AiJobsTray from '@/components/questions/AiJobsTray.vue';
import type { IconName } from '@/components/ui/icons';
import { PERM, type Permission } from '@kitobxonlik/contracts';
import { useSessionStore } from '@/stores/session.store';
import { useChromeStore } from '@/stores/chrome.store';
import { useLibraryStore } from '@/stores/library.store';
import { useAiTestsStore } from '@/stores/aiTests.store';
import { ORG_TOOLTIP } from '@/utils/format';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const session = useSessionStore();
const chrome = useChromeStore();
const library = useLibraryStore();
const aiTests = useAiTestsStore();

const navOpen = ref(false);
const stuck = ref(false);
const q = ref('');
const isDev = DEV_SESSION_ENABLED;
const loggingOut = ref(false);

/**
 * Chiqish: server cookie'larni o'chiradi, keyin mahalliy holat tozalanadi.
 * `session.logout()` xato bo'lsa ham sessiyani tozalaydi (`finally`), shuning
 * uchun bu yerda ham baribir kirish sahifasiga o'tamiz.
 */
async function logout(): Promise<void> {
  if (loggingOut.value) return;
  loggingOut.value = true;

  try {
    await session.logout();
    library.reset();
    aiTests.reset();
    await router.replace({ name: 'login' });
  } finally {
    loggingOut.value = false;
  }
}

interface NavItem {
  label: string;
  icon: IconName;
  to: { name: string; query?: Record<string, string> };
  match: (r: typeof route) => boolean;
  count?: number;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}

const groups = computed<NavGroup[]>(() => {
  const g: NavGroup[] = [];
  if (session.isStaff) {
    g.push({
      label: t('nav.library'),
      items: [
        {
          label: t('nav.home'),
          icon: 'home',
          to: { name: 'home' },
          match: (r) => r.name === 'home',
        },
        {
          label: t('library.catalog'),
          icon: 'grid',
          to: { name: 'library' },
          match: (r) => r.name === 'library' && !r.query.tab,
        },
        {
          label: t('library.favorites'),
          icon: 'bookmark',
          to: { name: 'library', query: { tab: 'favorites' } },
          match: (r) => r.query.tab === 'favorites',
          count: library.stats.favorites || undefined,
        },
        {
          label: t('library.enrolled'),
          icon: 'clock',
          to: { name: 'library', query: { tab: 'enrolled' } },
          match: (r) => r.query.tab === 'enrolled',
          count: library.stats.scheduled || undefined,
        },
      ],
    });
    const examItems: NavItem[] = [
      {
        label: t('nav.calendar'),
        icon: 'calendar',
        to: { name: 'calendar' },
        match: (r) => r.name === 'calendar',
      },
      {
        label: t('nav.myStats'),
        icon: 'chart',
        to: { name: 'my-stats' },
        match: (r) => r.name === 'my-stats',
      },
    ];

    // "Imtihonlarim" faqat imtihon topshira oladiganlarga
    if (session.can(PERM.EXAM_TAKE)) {
      examItems.unshift({
        label: t('nav.exams'),
        icon: 'award',
        to: { name: 'exams' },
        match: (r) => r.name === 'exams' || r.name === 'exam-result',
      });
    }

    g.push({ label: t('nav.exams'), items: examItems });
  }
  /*
   * Boshqaruv menyusi — HAR BIR BAND o'z ruxsatiga bog'langan (rol nomiga emas).
   * Admin rolga ruxsat qo'shsa/olib tashlasa menyu o'zi moslashadi; router
   * meta'sidagi ruxsat bilan bir xil bo'lishi shart, aks holda ko'ringan band
   * "Ruxsat yo'q" sahifasiga olib borardi.
   */
  const adminItems: Array<NavItem & { need: Permission }> = [
    {
      need: PERM.STATISTICS_READ,
      label: t('nav.statistics'),
      icon: 'trend',
      to: { name: 'admin-statistics' },
      match: (r) => r.name === 'admin-statistics',
    },
    {
      need: PERM.BOOK_READ,
      label: t('nav.books'),
      icon: 'book',
      to: { name: 'admin-books' },
      match: (r) => String(r.name).startsWith('admin-book') || r.name === 'admin-questions',
    },
    {
      need: PERM.EXAM_READ,
      label: t('nav.examsAdmin'),
      icon: 'list',
      to: { name: 'admin-exams' },
      match: (r) => r.name === 'admin-exams' || r.name === 'admin-exam',
    },
    {
      need: PERM.EXAM_READ,
      label: t('nav.calendar'),
      icon: 'calendar',
      to: { name: 'admin-calendar' },
      match: (r) => r.name === 'admin-calendar',
    },
    {
      need: PERM.ORGANIZATION_READ,
      label: t('nav.organizations'),
      icon: 'building',
      to: { name: 'admin-organizations' },
      match: (r) => r.name === 'admin-organizations' || r.name === 'admin-organization',
    },
    {
      need: PERM.DICTIONARY_READ,
      label: t('nav.dictionaries'),
      icon: 'layers',
      to: { name: 'admin-dictionaries' },
      match: (r) => r.name === 'admin-dictionaries',
    },
    {
      need: PERM.ROLE_READ,
      label: t('nav.roles'),
      icon: 'shield',
      to: { name: 'admin-roles' },
      match: (r) => r.name === 'admin-roles',
    },
    {
      need: PERM.USER_READ,
      label: t('nav.users'),
      icon: 'users',
      to: { name: 'admin-users' },
      match: (r) => r.name === 'admin-users',
    },
  ];

  const items = adminItems.filter((item) => session.can(item.need));
  if (items.length) g.push({ label: t('nav.admin'), items });

  return g;
});

const title = computed(() => chrome.title ?? (route.meta.title as string | undefined) ?? '');
const isAdminArea = computed(() => String(route.name ?? '').startsWith('admin'));

function submitSearch(): void {
  navOpen.value = false;
  void router.push({ name: 'library', query: { q: q.value.trim() || undefined } });
}

function onKey(e: KeyboardEvent): void {
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    const el = document.getElementById('shell-search') as HTMLInputElement | null;
    el?.focus();
    el?.select();
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKey);
  addEventListener('scroll', () => (stuck.value = scrollY > 8), { passive: true });
});

/* Layout sessiya tiklanishidan oldin mount bo'lishi mumkin (App.vue boshlang'ich
   route bilan layout tanlaydi) — shuning uchun rolga qarab, watch orqali yuklaymiz */
watch(
  () => session.isStaff,
  (staff) => {
    if (staff) {
      void library.loadAll();
      void library.loadIndex();
    }
  },
  { immediate: true },
);

watch(
  () => route.fullPath,
  () => {
    navOpen.value = false;
    chrome.reset();
    if (route.name === 'library' && typeof route.query.q === 'string') q.value = route.query.q;
  },
);
</script>

<template>
  <div class="shell" :class="{ 'nav-open': navOpen }">
    <a class="skip-link" href="#main">{{ t('common.skipToContent') }}</a>

    <!-- ==================== SIDEBAR ==================== -->
    <aside class="app-sidebar" id="sidebar">
      <RouterLink class="brand" :to="{ name: session.isStaff ? 'home' : 'admin-statistics' }">
        <span class="brand-mark"><AppIcon name="logo" :size="23" /></span>
        <span>
          <span class="brand-word">kitob<span>xonlik</span></span>
          <span class="brand__tag">{{ t('app.tagline') }}</span>
        </span>
      </RouterLink>

      <form
        v-if="session.isStaff"
        class="field search"
        role="search"
        @submit.prevent="submitSearch"
      >
        <AppIcon name="search" />
        <label class="sr-only" for="shell-search">{{ t('library.searchPlaceholder') }}</label>
        <input
          id="shell-search"
          v-model="q"
          type="search"
          :placeholder="t('library.searchShort')"
          autocomplete="off"
        />
        <kbd>Ctrl K</kbd>
      </form>

      <nav class="nav" :aria-label="t('common.mainMenu')">
        <template v-for="g in groups" :key="g.label">
          <p class="nav__label">{{ g.label }}</p>
          <ul>
            <li v-for="item in g.items" :key="item.label">
              <RouterLink :to="item.to" :class="{ 'is-active': item.match(route) }">
                <AppIcon :name="item.icon" :size="21" />
                {{ item.label }}
                <span v-if="item.count" class="count">{{ item.count }}</span>
              </RouterLink>
            </li>
          </ul>
        </template>
      </nav>

      <IndexCard v-if="session.isStaff" />

      <div class="app-sidebar__foot">
        <div class="app-sidebar__user">
          <Avatar :name="session.me?.name" :seed="session.me?.employee_id" :size="40" />
          <span class="min-w-0">
            <b class="block truncate font-semibold text-ink">{{ session.me?.name }}</b>
            <!-- Uzun nom 2 qatorgacha; sig'masa to'liq nomi tooltip'da (faqat kesilganda) -->
            <NEllipsis :line-clamp="2" :tooltip="ORG_TOOLTIP">{{
              session.me?.org_name || t('common.orgNumber', { id: session.orgId })
            }}</NEllipsis>
          </span>
        </div>

        <button
          type="button"
          class="btn btn--ghost app-sidebar__logout"
          :disabled="loggingOut"
          @click="logout"
        >
          <AppIcon name="logout" :size="18" />
          {{ t('session.logout') }}
        </button>
      </div>
    </aside>

    <div class="scrim" aria-hidden="true" @click="navOpen = false" />

    <!-- ==================== MAIN ==================== -->
    <div class="main">
      <header class="topbar" :class="{ 'is-stuck': stuck }">
        <button
          type="button"
          class="icon-btn topbar__burger"
          :aria-label="t('common.openMenu')"
          @click="navOpen = true"
        >
          <AppIcon name="menu" />
        </button>

        <nav class="crumbs" :aria-label="t('common.breadcrumbs')">
          <template v-if="chrome.crumbs.length">
            <template v-for="(c, i) in chrome.crumbs" :key="i">
              <RouterLink v-if="c.to" :to="c.to">{{ c.label }}</RouterLink>
              <span v-else>{{ c.label }}</span>
              <span aria-hidden="true">/</span>
            </template>
          </template>
          <template v-else-if="isAdminArea">
            <span>{{ t('nav.admin') }}</span>
            <span aria-hidden="true">/</span>
          </template>
          <b>{{ title }}</b>
        </nav>

        <div class="topbar__right">
          <RouterLink
            v-if="session.isStaff && isAdminArea"
            class="btn btn--ghost btn--sm topbar__switch"
            :to="{ name: 'home' }"
          >
            <AppIcon name="book" /> {{ t('nav.library') }}
          </RouterLink>
          <RouterLink
            v-else-if="session.canAdmin && !isAdminArea"
            class="btn btn--ghost btn--sm topbar__switch"
            :to="{ name: 'admin-statistics' }"
          >
            <AppIcon name="chart" /> {{ t('nav.admin') }}
          </RouterLink>
          <ThemeToggle />
        </div>
      </header>

      <main id="main" class="content">
        <slot />
      </main>

      <!-- Kitob AI'ga yuborilgan bo'lsa — jarayon har sahifada ko'rinadi -->
      <AiJobsTray v-if="session.can(PERM.QUESTION_READ)" />

      <footer class="footer">
        <span>{{ t('common.copyright', { year: new Date().getFullYear() }) }}</span>
        <a v-if="isDev" class="ml-auto" href="/dev/session">{{ t('common.devSession') }}</a>
      </footer>
    </div>
  </div>
</template>

<style scoped>
@reference '@/styles/theme.css';

.shell {
  display: grid;
  grid-template-columns: var(--sidebar-w) minmax(0, 1fr);
  min-height: 100vh;
}
.skip-link {
  position: fixed;
  top: var(--s-min);
  left: var(--s-min);
  z-index: 200;
  padding: var(--s-min) var(--s-3);
  border-radius: var(--r-sm);
  background: var(--brand-500);
  color: var(--paper-full);
  transform: translateY(-180%);
  transition: transform var(--dur) var(--ease);
}
.skip-link:focus {
  transform: none;
}

/* ---- sidebar: yuqorida logo va qidiruv, pastda ko'rsatkich kartasi va xodim —
   ular siqilmaydi; joy yetmasa faqat navigatsiya scroll bo'ladi.
   Klass `.sidebar` EMAS: pdf.js'ning global `.sidebar { width: 239px; … }`
   qoidasi o'qish oynasi bir marta ochilgach sahifada qoladi va menyuni
   toraytirib qo'yardi (pdfjsGlobalCss.spec.ts) ---- */
.app-sidebar {
  --fade: 28px; /* navigatsiya ro'yxatining pastki so'nishi */
  position: sticky;
  top: 0;
  align-self: start;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
  padding: var(--s-3) var(--s-min);
  background: var(--surface);
  border-right: var(--hair) solid var(--line);
  overflow: hidden;
  z-index: 40;
}
.app-sidebar > * {
  flex: 0 0 auto;
}
.app-sidebar .nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  /* pastda davomi borligini ko'rsatuvchi so'nish */
  mask-image: linear-gradient(to bottom, var(--ink-full) calc(100% - var(--fade)), transparent);
  padding-bottom: var(--s-3);
}
/* Past ekranlarda (odatiy noutbuk ham) butun sidebar scroll bo'ladi — punkt kesilmaydi */
@media (height <= theme(--screen-short)) {
  .app-sidebar {
    overflow-y: auto;
  }
  .app-sidebar .nav {
    flex: 0 0 auto;
    overflow: visible;
    mask-image: none;
  }
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--s-min);
  padding: 0 var(--s-icon);
}
.brand__tag {
  display: block;
  font-size: var(--fs-label);
  color: var(--ink-3);
  font-weight: var(--fw-medium);
}

.search kbd {
  position: absolute;
  right: var(--s-tight);
  top: 50%;
  transform: translateY(-50%);
  font: var(--fw-semibold) var(--fs-label) / 1 var(--font);
  color: var(--ink-3);
  background: var(--surface);
  border: var(--hair) solid var(--line);
  border-radius: var(--r-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
}
.search input:focus ~ kbd {
  display: none;
}

.nav__label {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--ink-3);
  padding: 0 var(--s-tight);
  margin: var(--s-min) 0 var(--s-icon);
}
.nav ul {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}
.nav a {
  display: flex;
  align-items: center;
  gap: var(--s-min);
  padding: var(--s-tight) var(--s-min);
  border-radius: var(--r-sm);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  color: var(--ink-2);
  transition:
    background var(--dur-fast) var(--ease),
    color var(--dur-fast) var(--ease);
}
.nav a:hover {
  background: var(--surface-2);
  color: var(--ink);
}
.nav a.is-active {
  background: var(--brand-50);
  color: var(--brand-600);
  font-weight: var(--fw-semibold);
}
.nav a .count {
  margin-left: auto;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--ink-3);
  background: var(--surface-2);
  border-radius: var(--r-pill);
  padding: var(--spacing-xxs) var(--s-icon);
}
.nav a.is-active .count {
  background: var(--surface);
  color: var(--brand-600);
}

.app-sidebar__foot {
  display: flex;
  flex-direction: column;
  gap: var(--s-tight);
  font-size: var(--fs-label);
  color: var(--ink-3);
  padding: var(--s-min) var(--s-icon) 0;
  border-top: var(--hair) solid var(--line);
}

/* Avatar + ism/tashkilot — bitta qator */
.app-sidebar__user {
  display: flex;
  align-items: center;
  gap: var(--s-tight);
  min-width: 0;
}

/* Chiqish — foydalanuvchi ma'lumoti OSTIDA, butun kenglikda */
.app-sidebar__logout {
  width: 100%;
}
/* Ikonka doim qizil — matn esa odatiy, tugma "xavfli" ko'rinib ketmasin */
.app-sidebar__logout svg {
  color: var(--danger);
}
.app-sidebar__logout:hover:not(:disabled) {
  color: var(--danger);
  border-color: var(--danger-line);
  background: var(--danger-soft);
}
.app-sidebar__logout:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ---- topbar ---- */
.main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: var(--s-min);
  padding: var(--s-min) var(--gutter);
  background: color-mix(in srgb, var(--bg) 84%, transparent);
  backdrop-filter: blur(var(--blur-bar));
  border-bottom: var(--hair) solid transparent;
  transition: border-color var(--dur) var(--ease);
}
.topbar.is-stuck {
  border-bottom-color: var(--line);
}
.topbar__burger {
  display: none;
}
.crumbs {
  display: flex;
  align-items: center;
  gap: var(--s-icon);
  min-width: 0;
  font-size: var(--fs-label);
  color: var(--ink-3);
  white-space: nowrap;
  overflow: hidden;
}
.crumbs a:hover {
  color: var(--brand-600);
}
.crumbs b {
  color: var(--ink);
  font-weight: var(--fw-semibold);
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--s-tight);
}

.content {
  width: 100%;
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.footer {
  border-top: var(--hair) solid var(--line);
  padding: var(--s-4) var(--gutter) var(--s-5);
  display: flex;
  flex-wrap: wrap;
  gap: var(--s-2) var(--s-6);
  align-items: center;
  color: var(--ink-3);
  font-size: var(--fs-label);
}
.footer a:hover {
  color: var(--brand-500);
}

/* ---- responsive ---- */
.scrim {
  position: fixed;
  inset: 0;
  z-index: 35;
  background: var(--scrim-nav);
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--dur) var(--ease),
    visibility var(--dur);
}
@media (width <= theme(--breakpoint-nav)) {
  .shell {
    grid-template-columns: 1fr;
  }
  .app-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: min(var(--width-xxs), calc(100vw - var(--s-6)));
    transform: translateX(-102%);
    transition: transform var(--dur-slow) var(--ease);
    box-shadow: var(--sh-4);
  }
  .nav-open .app-sidebar {
    transform: none;
  }
  .nav-open .scrim {
    opacity: 1;
    visibility: visible;
  }
  .topbar__burger {
    display: grid;
  }
}
@media (width <= theme(--breakpoint-mob)) {
  .topbar__switch {
    display: none;
  }
}
</style>
